import React, { useMemo } from 'react';
import { useTravels } from '../TravelContext';

// TL metninden sayısal değer çıkar ("30.000 TL" -> 30000)
const parseTL = (val) => {
  if (!val) return 0;
  const n = String(val).replace(/[^0-9.,]/g, '').replace(/\./g, '').replace(',', '.');
  const num = parseFloat(n);
  return isNaN(num) ? 0 : num;
};

// Sayıyı "12.345 TL" biçimine getir
const formatTL = (num) => new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', maximumFractionDigits: 0 }).format(num).replace('TRY', 'TL');

/* Harcamalarım – Gerçek veriler TravelContext'ten */
const Expenditures = () => {
  const { travels = [] } = useTravels();

  // Travel objelerinden harcama satırlarını türet
  const rows = useMemo(() => {
    return travels.map((t, idx) => {
      const route = t.route || '—';
      // Gün bilgisi: details.days (örn. "4 gün" veya "4 Gün")
      const days = t.details?.days || '—';
      // Tutar: öncelik planın tahmini_toplam_maliyet alanı -> yoksa details.budget (kişi başı)
      // details.budget genelde "20000 TL" gibi string; sayıya çeviriyoruz
      const est = t.details?.tahmini_toplam_maliyet || t.details?.estimatedCost;
      const amountNum = est ? Number(est) : parseTL(t.details?.budget);
      return {
        id: t.id || idx,
        route,
        days,
        amountNum,
      };
    });
  }, [travels]);

  const total = useMemo(() => rows.reduce((sum, r) => sum + (r.amountNum || 0), 0), [rows]);

  return (
    <>
      {/* Üst gradient bandı */}
      <div className="h-64 bg-gradient-to-r from-cyan-400 to-blue-500">
        <div
          className="h-full w-full bg-cover bg-center"
          style={{ backgroundImage: "url('https://www.transparenttextures.com/patterns/az-subtle.png')" }}
        />
      </div>

      {/* Zemin: açık temada gri‑50, koyu temada gri‑900 */}
      <div className="font-sans bg-gray-50 dark:bg-gray-900 min-h-[calc(100vh-16rem)] text-gray-900 dark:text-white flex flex-col items-center px-4 sm:px-6 lg:px-8 -mt-15 pb-16">
        <main className="w-full max-w-5xl">
          {/* Panel */}
          <section className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-3xl shadow-2xl p-6 sm:p-8 lg:p-10 -translate-y-16 transition-colors duration-500">
            {/* Başlık */}
            <div className="text-center mb-10">
              <h2 className="text-3xl sm:text-4xl font-semibold">Harcamalarım</h2>
              <p className="mt-2 text-gray-500 dark:text-blue-200 text-md">Planladığın seyahatlerden oluşan harcama özeti</p>
              <div className="mt-4 inline-flex items-center gap-3 bg-cyan-50 dark:bg-white/10 text-cyan-700 dark:text-cyan-200 px-4 py-2 rounded-full">
                <span className="text-sm">Toplam</span>
                <strong className="text-lg">{formatTL(total)}</strong>
              </div>
            </div>

            {/* Liste başlıkları */}
            <div className="hidden md:grid grid-cols-3 gap-4 px-4 pb-4 border-b border-gray-200 dark:border-white/20 text-sm">
              <span className="font-semibold text-gray-600 dark:text-gray-300">Rota</span>
              <span className="font-semibold text-gray-600 dark:text-gray-300 text-center">Gün</span>
              <span className="font-semibold text-gray-600 dark:text-gray-300 text-right">Tutar</span>
            </div>

            {/* Harcama listesi */}
            {rows.length === 0 ? (
              <p className="mt-6 text-center text-gray-500 dark:text-gray-300">Henüz kayıtlı bir seyahat yok. Önce bir plan oluştur.</p>
            ) : (
              <ul className="mt-6 space-y-4">
                {rows.map((r) => (
                  <li key={r.id} className="grid grid-cols-3 gap-4 items-center p-5 rounded-2xl bg-gray-100 dark:bg-slate-700 transition-colors duration-500">
                    <span className="font-bold text-gray-800 dark:text-white truncate">{r.route}</span>
                    <span className="text-center font-medium text-gray-700 dark:text-gray-200">{r.days}</span>
                    <span className="text-right font-bold text-lg text-cyan-600 dark:text-cyan-400">{formatTL(r.amountNum)}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </main>
      </div>
    </>
  );
};

export default Expenditures;
